import { ComponentProps, Ref, useCallback, useMemo, useRef } from 'react';
import tinycolor from 'tinycolor2';

import { volumeToLevel } from '~/modules/elements/volume-level';
import styles from '~/modules/game-engine/drawing/styles';
import { usePlayerMicData } from '~/modules/hooks/players/use-player-mic';
import { ONLINE_STATS_PUBLISH_MS } from '~/modules/online/protocol/consts';
import { PlayerNumber } from '~/modules/players/player-number';
import { twx } from '~/utils/twx';

const usePlayerColor = (playerNumber: PlayerNumber) => {
  return useMemo(() => {
    if (!styles.colors.players[playerNumber]) return '0, 0, 0';
    const rgb = tinycolor(styles.colors.players[playerNumber].text).toRgb();
    return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
  }, [playerNumber]);
};

const VolumeIndicatorBase = twx.div`pointer-events-none absolute top-0 right-0 z-1 h-full w-full origin-right overflow-hidden bg-repeat-y`;

interface Props extends ComponentProps<typeof VolumeIndicatorBase> {
  playerNumber: PlayerNumber;
  volume: number;
  ref?: Ref<HTMLDivElement | null>;
}

/**
 * Volume bar driven by an explicitly supplied level, for singers whose audio isn't on this device
 * (online rooms report their own volume). For local mics use `PlayerMicCheck` instead.
 */
export const VolumeIndicator = ({ volume, playerNumber, ref, ...rest }: Props) => {
  const percent = `${volumeToLevel(volume)}`;
  const color = usePlayerColor(playerNumber);

  return (
    // Same two-element shape as PlayerMicCheck below, and for the same reason: the scale has to sit
    // on an inner element. Scaling the rounded, clipping box itself squashes its corner radius along
    // with it, and the bar then bleeds past the corners of the row it's sitting in.
    <VolumeIndicatorBase {...rest} ref={ref}>
      <div
        className="h-full w-full origin-right"
        style={{
          transform: `scaleX(${percent})`,
          // Reported levels land a few times a second, which without this reads as a bar hopping
          // between positions — a linear tween over one reporting interval turns it into a meter
          transition: `transform ${ONLINE_STATS_PUBLISH_MS}ms linear`,
          background: `linear-gradient(270deg, rgba(${color}, 1) 0%, rgba(${color}, 0) 100%)`,
        }}
      />
    </VolumeIndicatorBase>
  );
};

export const PlayerMicCheck = ({
  playerNumber,
  ...props
}: { playerNumber: PlayerNumber } & ComponentProps<typeof VolumeIndicatorBase>) => {
  const elemRef = useRef<HTMLDivElement | null>(null);

  const cb = useCallback(([volume]: [number, number]) => {
    if (elemRef.current) {
      elemRef.current.style.transform = `scaleX(${volumeToLevel(volume)})`;
    }
  }, []);

  usePlayerMicData(playerNumber, cb);
  const color = usePlayerColor(playerNumber);

  return (
    <VolumeIndicatorBase {...props} data-test="mic-volume-indicator">
      <div
        className="h-full w-full origin-right"
        ref={elemRef}
        style={{
          background: `linear-gradient(270deg, rgba(${color}, 1) 0%, rgba(${color}, 0) 100%)`,
        }}
      />
    </VolumeIndicatorBase>
  );
};
